import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type AppointmentRow = Database['public']['Tables']['appointments']['Row'];
export type AppointmentStatus = Database['public']['Enums']['appointment_status'];
export type VisitStatus = Database['public']['Enums']['visit_status'];

export interface AppointmentListItem {
  id: string;
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  patient_name: string;
  clinician_id: string | null;
  clinician_name: string | null;
  service_id: string | null;
  service_name: string | null;
  service_color: string | null;
  starts_at: string;
  ends_at: string;
  status: AppointmentStatus;
  visit_status: VisitStatus | null;
  notes: string | null;
  recurrence_pattern?: string;
  recurrence_parent_id?: string | null;
  is_recurrence_parent?: boolean;
  payment_method?: string | null;
  payment_status?: string | null;
}

export interface ListFilters {
  start: string; // ISO
  end: string; // ISO
  clinicianId?: string | null;
}

export const appointmentsRangeQueryKey = (clinicId: string | null, filters: ListFilters) =>
  ['appointments', 'range', clinicId, filters] as const;

export const appointmentDetailQueryKey = (id: string | null | undefined) =>
  ['appointments', 'detail', id] as const;

export function useAppointmentsInRange(clinicId: string | null | undefined, filters: ListFilters) {
  return useQuery({
    queryKey: appointmentsRangeQueryKey(clinicId ?? null, filters),
    enabled: Boolean(clinicId && filters.start && filters.end),
    queryFn: async (): Promise<AppointmentListItem[]> => {
      if (!clinicId) return [];
      let query = supabase
        .from('appointments')
        .select(
          'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, visit_status, notes, ' +
            'recurrence_pattern, recurrence_parent_id, is_recurrence_parent, ' +
            'patient:patients!appointments_patient_id_fkey(first_name,last_name), ' +
            'clinician:users!appointments_clinician_id_fkey(name,email), ' +
            'service:services!appointments_service_id_fkey(color)',
        )
        .eq('clinic_id', clinicId)
        .gte('starts_at', filters.start)
        .lte('starts_at', filters.end)
        .neq('status', 'cancelled')
        .order('starts_at', { ascending: true });

      if (filters.clinicianId) {
        query = query.eq('clinician_id', filters.clinicianId);
      }

      const { data, error } = await query;
      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string | null; // Nullable for blocker appointments
        clinician_id: string | null;
        service_id: string | null;
        service_name: string | null;
        starts_at: string;
        ends_at: string;
        status: AppointmentStatus;
        visit_status: VisitStatus | null;
        notes: string | null;
        recurrence_pattern?: string;
        recurrence_parent_id?: string | null;
        is_recurrence_parent?: boolean;
        patient: { first_name: string; last_name: string } | null;
        clinician: { name: string | null; email: string } | null;
        service: { color: string | null } | null;
      };

      return ((data ?? []) as unknown as Joined[]).map(
        (r): AppointmentListItem => ({
          id: r.id,
          clinic_id: r.clinic_id,
          patient_id: r.patient_id,
          patient_name: r.patient ? `${r.patient.first_name} ${r.patient.last_name}`.trim() : '—',
          clinician_id: r.clinician_id,
          clinician_name: r.clinician?.name ?? r.clinician?.email ?? null,
          service_id: r.service_id,
          service_name: r.service_name,
          service_color: r.service?.color ?? null,
          starts_at: r.starts_at,
          ends_at: r.ends_at,
          status: r.status,
          visit_status: r.visit_status,
          notes: r.notes,
          recurrence_pattern: r.recurrence_pattern,
          recurrence_parent_id: r.recurrence_parent_id,
          is_recurrence_parent: r.is_recurrence_parent,
        }),
      );
    },
    staleTime: 15_000,
  });
}

export function useAppointment(id: string | null | undefined) {
  return useQuery({
    queryKey: appointmentDetailQueryKey(id ?? null),
    enabled: Boolean(id),
    queryFn: async (): Promise<AppointmentListItem> => {
      if (!id) throw new Error('No id');
      const { data, error } = await supabase
        .from('appointments')
        .select(
          'id, clinic_id, patient_id, clinician_id, service_id, service_name, starts_at, ends_at, status, visit_status, notes, ' +
            'recurrence_pattern, recurrence_parent_id, is_recurrence_parent, ' +
            'patient:patients!appointments_patient_id_fkey(first_name,last_name), ' +
            'clinician:users!appointments_clinician_id_fkey(name,email), ' +
            'service:services!appointments_service_id_fkey(color)',
        )
        .eq('id', id)
        .single();
      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        patient_id: string | null; // Nullable for blocker appointments
        clinician_id: string | null;
        service_id: string | null;
        service_name: string | null;
        starts_at: string;
        ends_at: string;
        status: AppointmentStatus;
        visit_status: VisitStatus | null;
        notes: string | null;
        recurrence_pattern?: string;
        recurrence_parent_id?: string | null;
        is_recurrence_parent?: boolean;
        patient: { first_name: string; last_name: string } | null;
        clinician: { name: string | null; email: string } | null;
        service: { color: string | null } | null;
      };

      const r = data as unknown as Joined;
      return {
        id: r.id,
        clinic_id: r.clinic_id,
        patient_id: r.patient_id,
        patient_name: r.patient ? `${r.patient.first_name} ${r.patient.last_name}`.trim() : '—',
        clinician_id: r.clinician_id,
        clinician_name: r.clinician?.name ?? r.clinician?.email ?? null,
        service_id: r.service_id,
        service_name: r.service_name,
        service_color: r.service?.color ?? null,
        starts_at: r.starts_at,
        ends_at: r.ends_at,
        status: r.status,
        visit_status: r.visit_status,
        notes: r.notes,
        recurrence_pattern: r.recurrence_pattern,
        recurrence_parent_id: r.recurrence_parent_id,
        is_recurrence_parent: r.is_recurrence_parent,
      };
    },
  });
}

export interface CreateAppointmentInput {
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  starts_at: string;
  ends_at: string;
  service_id?: string | null;
  clinician_id?: string | null;
  notes?: string | null;
}

export interface CreateRecurringAppointmentInput {
  clinic_id: string;
  patient_id: string | null; // Nullable for blocker appointments
  starts_at: string;
  ends_at: string;
  service_id?: string | null;
  clinician_id?: string | null;
  notes?: string | null;
  recurrence_pattern: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  recurrence_end_date?: string | null;
  recurrence_count?: number | null;
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAppointmentInput): Promise<AppointmentRow> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await supabase.rpc('create_appointment', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id ?? undefined,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_service_id: input.service_id ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
        p_notes: input.notes ?? undefined,
      } as any);
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useCreateRecurringAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRecurringAppointmentInput): Promise<AppointmentRow[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await supabase.rpc('create_recurring_appointment_series', {
        p_clinic_id: input.clinic_id,
        p_patient_id: input.patient_id ?? undefined,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_service_id: input.service_id ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
        p_notes: input.notes ?? undefined,
        p_recurrence_pattern: input.recurrence_pattern,
        p_recurrence_end_date: input.recurrence_end_date ?? undefined,
        p_recurrence_count: input.recurrence_count ?? undefined,
      } as any);
      if (error) throw error;
      return data as AppointmentRow[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export interface RescheduleAppointmentInput {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  clinician_id?: string | null;
  reason?: string | null;
}

export function useRescheduleAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RescheduleAppointmentInput): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('reschedule_appointment', {
        p_appointment_id: input.appointment_id,
        p_starts_at: input.starts_at,
        p_ends_at: input.ends_at,
        p_clinician_id: input.clinician_id ?? undefined,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      reason?: string | null;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('cancel_appointment', {
        p_appointment_id: input.appointment_id,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
      qc.invalidateQueries({ queryKey: ['appointments', 'patient', row.patient_id] });
    },
  });
}

export function useCompleteAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('complete_appointment', {
        p_appointment_id: id,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useMarkNoShowAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('mark_no_show_appointment', {
        p_appointment_id: id,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useUpdateAppointmentNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      notes: string;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('update_appointment_notes', {
        p_appointment_id: input.appointment_id,
        p_notes: input.notes,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useUpdateAppointmentDuration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      new_ends_at: string;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('update_appointment_duration', {
        p_appointment_id: input.appointment_id,
        p_new_ends_at: input.new_ends_at,
      });
      if (error) throw error;
      return data as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

export function useDeleteAppointmentSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      delete_future_only: boolean;
      reason?: string | null;
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('delete_appointment_series', {
        p_appointment_id: input.appointment_id,
        p_delete_future_only: input.delete_future_only,
        p_reason: input.reason ?? undefined,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: (_count, variables) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(variables.appointment_id) });
    },
  });
}

export function useUpdateAppointmentSeries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      update_future_only: boolean;
      notes?: string | null;
      clinician_id?: string | null;
    }): Promise<number> => {
      const { data, error } = await supabase.rpc('update_appointment_series', {
        p_appointment_id: input.appointment_id,
        p_update_future_only: input.update_future_only,
        p_notes: input.notes ?? undefined,
        p_clinician_id: input.clinician_id ?? undefined,
      });
      if (error) throw error;
      return data as number;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useSetVisitStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      appointment_id: string;
      visit_status: VisitStatus;
    }): Promise<AppointmentRow> => {
      const { data, error } = await supabase.rpc('set_appointment_visit_status', {
        p_appointment_id: input.appointment_id,
        p_visit_status: input.visit_status,
      });
      if (error) throw error;
      return data as unknown as AppointmentRow;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: appointmentDetailQueryKey(row.id) });
    },
  });
}

/**
 * Subscribes to Realtime INSERT/UPDATE/DELETE on the appointments table for
 * the active clinic and invalidates the visible range query on any change.
 * Call once per calendar mount; cleans up on unmount.
 */
export function useAppointmentsRealtime(clinicId: string | null | undefined, filters: ListFilters) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!clinicId) return;
    const channel = supabase
      .channel(`appointments:${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'appointments',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({
            queryKey: appointmentsRangeQueryKey(clinicId, filters),
          });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, filters.start, filters.end, filters.clinicianId, qc]);
}
